import { useRouter } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Users01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useMerchantRiskCatalog } from "@/api/merchant-risks";
import { useMerchantAdmins } from "@/api/team";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { buildRiskGroupsFromCatalog } from "@/components/safety-config/risk-groups";
import { usePermissions } from "@/hooks/use-permissions";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";

/**
 * The merchant's risk catalog grouped for a risk picker, with whether each entry is monitored. A
 * picker entry can merge several risks that share a name; it counts as monitored when any of them
 * is switched on.
 */
export const useRiskMonitoring = () => {
  const { data: catalog } = useMerchantRiskCatalog();

  return useMemo(() => {
    const rows = catalog ?? [];
    const groups = buildRiskGroupsFromCatalog(rows);
    const enabledIds = new Set(rows.filter((row) => row.enabled).map((row) => row.id));
    const items = groups.flatMap((group) => group.items);
    const itemFor = (riskId: string) => items.find((item) => item.key === riskId || item.keys?.includes(riskId));
    const isMonitored = (riskId: string) => (itemFor(riskId)?.keys ?? [riskId]).some((id) => enabledIds.has(id));
    return { groups, itemFor, isMonitored, isLoaded: !!catalog };
  }, [catalog]);
};

export const MonitoringDot = ({ monitored }: { monitored: boolean }) => (
  <span
    aria-hidden="true"
    className={cx("size-2 shrink-0 rounded-full", monitored ? "bg-success-solid" : "bg-utility-gray-300")}
  />
);

/** The dot on a picked risk, which names its monitoring state on hover. */
export const MonitoringDotWithTooltip = ({ riskName, monitored }: { riskName: string; monitored: boolean }) => (
  <Tooltip title={`${riskName} ${monitored ? "is being monitored" : "is not being monitored"}`} placement="top">
    <TooltipTrigger aria-label={`${riskName} monitoring status`} className="flex size-3 items-center justify-center">
      <MonitoringDot monitored={monitored} />
    </TooltipTrigger>
  </Tooltip>
);

const MerchantAdminsList = () => {
  const [open, setOpen] = useState(false);
  const { admins } = useMerchantAdmins();

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-fit cursor-pointer items-center gap-1 text-sm font-semibold text-brand-secondary"
      >
        {open ? "Hide admins" : "See all admins"}
        {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
      </button>
      {open && (
        <ul className="mt-2 flex flex-col">
          {admins.map((admin) => (
            <li
              key={admin.id}
              className="flex items-center justify-between gap-3 border-t border-secondary py-2 text-sm text-secondary"
            >
              <span className="truncate">{`${admin.firstName} ${admin.lastName}`.trim() || admin.email}</span>
              <a href={`mailto:${admin.email}`} className="truncate text-tertiary underline">
                {admin.email}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type RiskMonitoringTipProps = {
  riskId: string;
  riskName: string;
  monitored: boolean;
  /** Called once the reviewer leaves to switch the risk on or off in Configure Safety. */
  onOpenConfigure?: () => void;
};

/**
 * Says whether a risk is monitored and offers to change it. Admins get a link that opens Configure
 * Safety in a new tab on that risk; everyone else is told to ask an admin, and can list them.
 */
export const RiskMonitoringTip = ({ riskId, riskName, monitored, onOpenConfigure }: RiskMonitoringTipProps) => {
  const { canConfigureSafety } = usePermissions();
  const merchantId = useMerchant();
  const router = useRouter();

  const configureHref = router.buildLocation({
    to: "/merchant/$merchantId/safety/configure",
    params: { merchantId },
    search: { focusRisk: riskId, focusAction: monitored ? "off" : "on" },
  }).href;

  return (
    <div className="flex gap-2 rounded-lg bg-unbox-cream-50 p-3">
      <Users01 aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-secondary" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-primary">
            {riskName} {monitored ? "is being monitored" : "isn't being monitored"}
          </span>
          <span className="text-sm text-tertiary">
            {monitored ? "Your videos are being checked for this." : "Your videos aren't being checked for this."}
            {canConfigureSafety &&
              (monitored
                ? " If it's fine for your brand, stop monitoring it."
                : " If it matters to your brand, start monitoring it.")}
          </span>
        </div>
        {canConfigureSafety ? (
          <Button
            color="secondary"
            size="sm"
            className="w-fit"
            href={configureHref}
            target="_blank"
            rel="noopener"
            onClick={onOpenConfigure}
          >
            {monitored ? "Stop monitoring" : "Start monitoring"}
          </Button>
        ) : (
          <>
            <span className="text-sm text-tertiary">
              You don't have permission to turn {monitored ? "off" : "on"} monitoring for this risk. Contact an admin to
              turn it {monitored ? "off" : "on"}.
            </span>
            <MerchantAdminsList />
          </>
        )}
      </div>
    </div>
  );
};
